import { booleanLookup } from '../../../common/constants/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { IApiResponseBody, ISearchResponse } from '../../../common/models/common';
import { IAzureReservationOrders } from '../../../services/azure/azureReservationOrders/azureReservationOrders.model';
import { RootState } from '../../app.model';
import {
  searchAzureReservationOrders,
  getAzureReservationOrdersById,
  saveAzureReservationOrders,
  deleteAzureReservationOrders,
} from './azureReservationOrders.action';
import { IAzureReservationOrdersState } from './azureReservationOrders.model';

export const initialState: IAzureReservationOrdersState = {
  search: {
    loading: false,
    hasErrors: false,
    data: [],
    count: 0,
    lookups: {},
    tableName: '',
  },
  tableColumnSelection: {
    id: null,
    table_name: null,
    columns: {},
  },
  getById: {
    loading: false,
    hasErrors: false,
    data: null,
  },
  save: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
  delete: {
    loading: false,
    hasErrors: false,
    messages: [],
  },
};

export const azureReservationOrdersSlice = createSlice({
  name: 'azureReservationOrders',
  initialState,
  reducers: {
    clearAzureReservationOrders: () => {
      return initialState;
    },
    clearAzureReservationOrdersMessages: (state) => {
      state.save.messages = [];
      state.delete.messages = [];
    },
    clearAzureReservationOrdersGetById: (state) => {
      state.getById.data = null;
    },
    setTableColumnSelection: (state, action: PayloadAction<{ [key: string]: boolean }>) => {
      state.tableColumnSelection.columns = action.payload;
    },
  },
  extraReducers: {
    // Search
    [searchAzureReservationOrders.pending.type]: (state) => {
      state.search.loading = true;
    },
    [searchAzureReservationOrders.fulfilled.type]: (
      state,
      action: PayloadAction<ISearchResponse<IAzureReservationOrders>>
    ) => {
      const { search_result, ...rest } = action.payload;
      state.search.data = search_result.records;
      state.search.count = search_result.total_count;
      if (JSON.stringify(rest) !== '{}') {
        state.search.lookups = { ...rest, booleanLookup };
      }
      state.search.loading = false;
      state.search.hasErrors = false;
      state.search.tableName = search_result.table_name;
      if (search_result.column_selection) {
        state.tableColumnSelection.id = search_result.column_selection.id;
        const tableSelectionObj = JSON.parse(search_result.column_selection.columns as any);
        if (tableSelectionObj.columns) {
          state.tableColumnSelection.column_orders = tableSelectionObj.column_orders;
          state.tableColumnSelection.columns = tableSelectionObj.columns;
        } else {
          state.tableColumnSelection.columns = tableSelectionObj;
        }
      }
      state.tableColumnSelection.table_name = search_result.table_name;
    },
    [searchAzureReservationOrders.rejected.type]: (state) => {
      state.search.loading = false;
      state.search.hasErrors = true;
    },

    // Get by id
    [getAzureReservationOrdersById.pending.type]: (state) => {
      state.getById.loading = true;
    },
    [getAzureReservationOrdersById.fulfilled.type]: (
      state,
      action: PayloadAction<IAzureReservationOrders>
    ) => {
      state.getById.data = action.payload;
      state.getById.loading = false;
      state.getById.hasErrors = false;
    },
    [getAzureReservationOrdersById.rejected.type]: (state) => {
      state.getById.loading = false;
      state.getById.hasErrors = true;
    },

    // Save
    [saveAzureReservationOrders.pending.type]: (state) => {
      state.save.loading = true;
      state.save.messages = [];
    },
    [saveAzureReservationOrders.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.save.loading = false;
      state.save.hasErrors = false;
      state.save.messages = action.payload.messages;
    },
    [saveAzureReservationOrders.rejected.type]: (state) => {
      state.save.loading = false;
      state.save.hasErrors = true;
    },

    // Delete
    [deleteAzureReservationOrders.pending.type]: (state) => {
      state.delete.loading = true;
      state.delete.messages = [];
    },
    [deleteAzureReservationOrders.fulfilled.type]: (
      state,
      action: PayloadAction<IApiResponseBody<unknown>>
    ) => {
      state.delete.loading = false;
      state.delete.hasErrors = false;
      state.delete.messages = action.payload.messages;
    },
    [deleteAzureReservationOrders.rejected.type]: (state) => {
      state.delete.loading = false;
      state.delete.hasErrors = true;
    },
  },
});

// A selector
export const azureReservationOrdersSelector = (state: RootState) => state.azureReservationOrders;

// Actions
export const {
  clearAzureReservationOrders,
  clearAzureReservationOrdersMessages,
  clearAzureReservationOrdersGetById,
  setTableColumnSelection,
} = azureReservationOrdersSlice.actions;

// The reducer
export default azureReservationOrdersSlice.reducer;
